import { analyticsClient } from "./client";

/**
 * EXCLUDED_EMAIL_DOMAINS are the domains whose signed-in people are excluded from
 * PostHog entirely — no events, no session replay. These are Mountaya's own
 * addresses: internal use of the product answers no product question, and left
 * unfiltered it is the loudest traffic in the project because the team opens the
 * studio far more often than any customer does.
 */
const EXCLUDED_EMAIL_DOMAINS = ["mountaya.com"] as const;

/**
 * ANALYTICS_OPT_OUT_STORAGE_KEY is the localStorage key remembering that this
 * device belongs to an excluded person. Namespaced under `mountaya.analytics.` so
 * it never collides with other persisted client state.
 */
const ANALYTICS_OPT_OUT_STORAGE_KEY = "mountaya.analytics.opt_out";

/**
 * OPTED_OUT is the only value ever written under the key. Presence alone would be
 * enough, but storing an explicit marker keeps a hand-edited value from reading as
 * an opt-out.
 */
const OPTED_OUT = "1";

/**
 * isExcludedEmail reports whether an address belongs to an excluded domain, or to
 * any subdomain of one. The comparison is case-insensitive and anchored on the
 * address's host, so `rider@notmountaya.com` and `rider@mountaya.com.example.org`
 * are both ordinary visitors.
 */
export const isExcludedEmail = (email: string | null | undefined): boolean => {
  if (!email) {
    return false;
  }

  // The local part may itself contain an "@" when quoted, so the host is what
  // follows the LAST one.
  const address = email.trim().toLowerCase();
  const separator = address.lastIndexOf("@");
  if (separator === -1) {
    return false;
  }

  const host = address.slice(separator + 1);

  return EXCLUDED_EMAIL_DOMAINS.some((domain) => host === domain || host.endsWith(`.${domain}`));
};

/**
 * readAnalyticsOptOut reports whether this device has already been opted out. It
 * is read at bootstrap, before the SDK loads, so an excluded person never fetches
 * the posthog-js chunk again after the visit that classified them.
 *
 * A missing window (a build step importing this module) or a throwing read
 * (Safari private mode raises on access) reads as "not opted out": the identity
 * that arrives moments later re-applies the policy anyway, so the failure costs a
 * partial session rather than correctness.
 *
 * Storage is reached through `globalThis.window` rather than a bare `localStorage`
 * on purpose. Node ships an inert Web Storage global that shadows the test
 * environment's, and this package's vitest run has no setup file installing a shim
 * over it.
 */
export const readAnalyticsOptOut = (): boolean => {
  try {
    return globalThis.window?.localStorage.getItem(ANALYTICS_OPT_OUT_STORAGE_KEY) === OPTED_OUT;
  } catch {
    return false;
  }
};

/**
 * optOutAnalytics excludes this device from PostHog and remembers the decision.
 *
 * The order of the two SDK calls is load-bearing. opt_out_capturing only disables
 * capture and persistence; it leaves the recorder running in this configuration,
 * so stopSessionRecording is what actually stops rrweb observing the page. And
 * stopping flushes the recorder's buffer rather than discarding it, so the opt-out
 * has to be in effect first: capture drops every event once the SDK is opted out,
 * `$snapshot` included, which is what keeps that final flush from reaching PostHog.
 *
 * To undo this on your own machine — to verify the integration against the real
 * project — run `posthog.clear_opt_in_out_capturing()` in the console, remove the
 * `mountaya.analytics.opt_out` key from localStorage, and reload.
 */
export const optOutAnalytics = (): void => {
  writeAnalyticsOptOut();

  const client = analyticsClient();
  client?.opt_out_capturing();
  client?.stopSessionRecording();
};

/**
 * clearAnalyticsOptOut readmits this device. It clears both the local memo and
 * PostHog's own persisted consent, which are always written and cleared together
 * so the two cannot disagree about whether this browser reports.
 *
 * It runs on logout as well as on an identity that is no longer excluded: a
 * shared browser must not stay silent for whoever signs in next.
 */
export const clearAnalyticsOptOut = (): void => {
  try {
    globalThis.window?.localStorage.removeItem(ANALYTICS_OPT_OUT_STORAGE_KEY);
  } catch {
    // Storage unavailable (private mode): nothing was persisted to begin with, so
    // there is nothing to undo. The SDK-side clear below still runs.
  }

  analyticsClient()?.clear_opt_in_out_capturing();
};

/**
 * writeAnalyticsOptOut persists the decision. It is best-effort: a storage failure
 * costs the memo, not the opt-out itself, which the SDK persists on its own for
 * the remainder of the visit.
 */
const writeAnalyticsOptOut = (): void => {
  try {
    globalThis.window?.localStorage.setItem(ANALYTICS_OPT_OUT_STORAGE_KEY, OPTED_OUT);
  } catch {
    // Storage unavailable (private mode / quota): the next page load re-applies
    // the policy as soon as the session resolves. No user-facing failure.
  }
};
